import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import { StepProps, roadSourceIdsPayload } from "../types";
import { useAddProject } from "../../../api/projects";
import { useNavigate } from "react-router-dom";
import { BASE_TILE_METERS, PROJECT_SIZE_GRID_DIM } from "../../../constants/project";
import { useDataSourcesByBbox } from "../../../api/dataSources";
import { ProjectBoundary } from "../../../types/project";
import { useAuth } from "../../../contexts/AuthContext";
import { useProjectFolders } from "../../../api/projectFolders";
import { buildFolderIndex, pathLabel } from "../../projectListing/folderTree";

const bboxToPolygon = (top: number, left: number, bottom: number, right: number): ProjectBoundary => ({
  type: "Polygon",
  coordinates: [[[left, bottom], [right, bottom], [right, top], [left, top], [left, bottom]]],
});

interface FinalReviewStepProps extends StepProps {
  /** Called once the project is created, in place of going to the listing
   *  (the listing's own new-project panel is already there). */
  onCreated?: () => void;
}

const FinalReviewStep: React.FC<FinalReviewStepProps> = ({
  data,
  onBack,
  onCreated,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Expansion uses the capability-gated /expand endpoint; a 403 would force a
  // logout, so only attempt it for users the backend will accept.
  const canExpand =
    !!user?.is_staff ||
    !!user?.capabilities?.some((cap) => cap.codename === "infinite_project");
  const { mutateAsync: createProject } = useAddProject();
  // Where the listing will file the new project (null = top level).
  const { folders } = useProjectFolders();
  const folderLabel = data.folderId ? pathLabel(buildFolderIndex(folders), data.folderId) : "";
  // Covers the whole submit (create + sequential tile expansions), since the
  // mutation's own pending flag clears the moment the create call resolves.
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  // The selected size is an NxN grid of base tiles, fanned out during init.
  const gridDim = PROJECT_SIZE_GRID_DIM[data.projectSize] ?? 1;
  // Manually-added tiles beyond that grid are sent in the create payload and
  // ingested by the SAME tiled-init pipeline as the grid (no post-create
  // /expand), so a single finalize covers them and the project never shows the
  // live "expanding" status mid-create.
  const extraTiles = (data.expansionTiles ?? []).filter(
    (t) => !(t.col >= 0 && t.col < gridDim && t.row >= 0 && t.row < gridDim)
  );

  // Calculate bounding box for project creation
  const bufferLatLng = (
    lat: number,
    lng: number,
    bufferMetersY: number,
    bufferMetersX: number
  ) => {
    const earthRadius = 6371000;
    const latBuffer = (bufferMetersY / earthRadius) * (180 / Math.PI);
    const lngBuffer = (bufferMetersX / earthRadius) * (180 / Math.PI) / Math.cos(lat * Math.PI / 180);

    return {
      top: lat + latBuffer,
      bottom: lat - latBuffer,
      left: lng - lngBuffer,
      right: lng + lngBuffer,
    };
  };

  const calculateBoundingBox = () => {
    if (!data.selectedLatLng) return { top: 0, right: 0, bottom: 0, left: 0 };

    // Send the base (small) tile bbox as `location`; the backend tiles it into
    // the size's NxN grid. The area can be grown further via tile expansion.
    const { x: bufferX, y: bufferY } = BASE_TILE_METERS;
    return bufferLatLng(data.selectedLatLng.lat, data.selectedLatLng.lng, bufferY, bufferX);
  };

  const boundingBox = calculateBoundingBox();
  const { top, right, bottom, left } = boundingBox;

  // Fetch source data for name lookups
  const { dataSources: vegetationSources } = useDataSourcesByBbox('vegetation', boundingBox);
  const { dataSources: fireHistorySources } = useDataSourcesByBbox('fire_history', boundingBox);
  const { dataSources: roadSources } = useDataSourcesByBbox('roads', boundingBox);
  const roadSourceName = roadSources.find((src) => String(src.id) === data.roadSource)?.name;

  const onSubmit = async () => {
    if (!data.selectedLatLng || !data.vegetationSource || !data.fireHistorySource) {
      console.error("Missing required data for project creation");
      return;
    }

    setIsCreatingProject(true);
    try {
      // Create one project as the base square (tile 0,0). Its location bbox
      // becomes the tile size; expansions reuse that same grid.
      const formData = new FormData();

      const projectData = {
        name: data.name,
        description: data.description,
        vegetationSourceId: data.vegetationSource,
        fireHistorySourceId: data.fireHistorySource,
        location: bboxToPolygon(top, left, bottom, right),
        projectSize: data.projectSize,
        assetSourceIds: data.selectedAssetSources.map((source) => source.id),
        treatmentSourceIds: data.selectedTreatmentSources.map((source) => source.id),
        propertySourceIds: data.selectedPropertySources.map((source) => source.id),
        stampRoadsIntoVegetation: data.stampRoadsIntoVegetation,
        roadSourceIds: roadSourceIdsPayload(data),
        modelUrbanEnvironment: data.modelUrbanEnvironment,
        defaultConstructionTypeId:
          data.modelUrbanEnvironment && data.defaultConstructionTypeId !== ""
            ? data.defaultConstructionTypeId
            : null,
        tags: data.tags || [],
        folderId: data.folderId ?? null,
        // Manual expansion tiles ingested as part of init (capability-gated on
        // the backend, mirroring the live /expand endpoint).
        expansionTiles: (canExpand ? extraTiles : []).map((t) => ({
          col: t.col,
          row: t.row,
        })),
      };

      formData.append("data", JSON.stringify(projectData));

      // Add property files
      if (data.propertyFiles) {
        if (data.propertyFiles instanceof File) {
          formData.append("propertyFiles", data.propertyFiles);
        } else {
          formData.append("propertyFileId", String((data.propertyFiles as any).id));
        }
      }

      // Add fuel disruption file
      if (data.fuelDisruptionFile) {
        formData.append("fuelDisruptionFile", data.fuelDisruptionFile);
      }

      await createProject(formData);

      if (onCreated) onCreated();
      else navigate(`/projects?refresh=true${data.folderId ? `&folder=${data.folderId}` : ""}`);
    } catch (error) {
      console.error("Error creating project:", error);
      setIsCreatingProject(false);
    }
  };

  const totalTiles = gridDim * gridDim + extraTiles.length;
  const isTiled = totalTiles > 1;

  return (
    <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ p: 3, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Review & Create
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#666",
          }}
        >
          Review your project configuration and create your project{isTiled ? ` (${totalTiles} tiles)` : ''}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, p: 3 }}>
        <Box sx={{ maxWidth: 800, mx: "auto" }}>
          {/* Project Summary */}
          <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: "#1a1a1a",
                }}
              >
                Project Summary
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Project Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.name}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Description
                </Typography>
                <Typography variant="body1">
                  {data.description || "No description provided"}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Folder
                </Typography>
                <Typography variant="body1">
                  {folderLabel || "All projects (top level)"}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Project Configuration
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={`Size: ${data.projectSize}`} size="small" />
                  <Chip label="Shape: Square" size="small" />
                  {isTiled && (
                    <Chip
                      label={`Expanded: ${totalTiles} tiles`}
                      size="small"
                      color="primary"
                    />
                  )}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Location
                </Typography>
                <Typography variant="body1">
                  {data.selectedLatLng ?
                    `${data.selectedLatLng.lat.toFixed(4)}, ${data.selectedLatLng.lng.toFixed(4)}` :
                    "No location selected"
                  }
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Data Sources
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  {data.vegetationSource && (
                    <Chip
                      label={vegetationSources.find((src) => String(src.id) === String(data.vegetationSource))?.name || "Vegetation"}
                      size="small"
                      color="success"
                    />
                  )}
                  {data.fireHistorySource && (
                    <Chip
                      label={fireHistorySources.find((src) => String(src.id) === String(data.fireHistorySource))?.name || "Fire History"}
                      size="small"
                      color="warning"
                    />
                  )}
                  {data.stampRoadsIntoVegetation && (
                    <Chip
                      label={`Roads cut: ${roadSourceName ?? (data.roadSource ? "selected source" : "built-in")}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Asset Sources ({data.selectedAssetSources.length})
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  {data.selectedAssetSources.map((source) => (
                    <Chip key={source.id} label={source.name} size="small" />
                  ))}
                </Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Pre-loaded Treatment Sources ({data.selectedTreatmentSources.length})
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  {data.selectedTreatmentSources.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">None selected</Typography>
                  ) : (
                    data.selectedTreatmentSources.map((source) => (
                      <Chip key={source.id} label={source.name} size="small" color="success" variant="outlined" />
                    ))
                  )}
                </Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Property Boundary Sources ({data.selectedPropertySources.length})
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {data.selectedPropertySources.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">None selected</Typography>
                  ) : (
                    data.selectedPropertySources.map((source) => (
                      <Chip key={source.id} label={source.name} size="small" color="info" variant="outlined" />
                    ))
                  )}
                </Box>
              </Box>

              {data.tags && data.tags.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Tags ({data.tags.length})
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {data.tags.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" sx={{ backgroundColor: "#e0f2fe", color: "#0369a1" }} />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {(data.propertyFiles || data.fuelDisruptionFile) && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Uploaded Files
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {data.propertyFiles && (
                        <Chip
                          label={(data.propertyFiles as any).name ?? (data.propertyFiles as any).original_filename}
                          size="small"
                          color="info"
                        />
                      )}
                      {data.fuelDisruptionFile && (
                        <Chip label={data.fuelDisruptionFile.name} size="small" color="info" />
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Footer - Navigation (sticky so Create stays visible while scrolling) */}
      <Box
        sx={{
          p: 3,
          backgroundColor: "#f8f9fa",
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          bottom: 0,
          zIndex: 1000,
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={isCreatingProject}
          sx={{
            borderColor: "#e0e0e0",
            color: "#666",
            "&:hover": {
              backgroundColor: "#f5f5f5",
              borderColor: "#ccc",
            },
          }}
        >
          Previous
        </Button>

        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isCreatingProject}
          sx={{
            backgroundColor: "#00d4ad",
            "&:hover": {
              backgroundColor: "#00b89c",
            },
            minWidth: 140,
          }}
        >
          {isCreatingProject ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
              Creating...
            </>
          ) : (
            "Create Project"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default FinalReviewStep;
